import { DateTime } from "luxon";

import { PersonalProfile, TimeDependentSegmentPayload } from "@/lib/drivers/tandem/commands/PersonalProfile";
import { TandemConstants } from "@/lib/drivers/tandem/TandemConstants";
import { TandemData, TandemLastUploadInfo } from "@/lib/drivers/tandem/TandemTypes";
import { TandemTransformer } from "@/lib/drivers/tandem/transformers/TandemTransformer";
import { getBasalInsulinPerDay } from "@/lib/utils/basalInsulinPerDay";
import { InsulinPumpProfileDTO, InsulinPumpSegmentDTO, MedicalDeviceDTO } from "@/models/DeviceModel";
import { DateUtils } from "@/utils/dateUtils";

type Devices = {
  pumpDevice: MedicalDeviceDTO;
  cgmDevice: MedicalDeviceDTO;
};

export class TandemDeviceTransformer implements TandemTransformer<Devices> {
  _getSegment =
    (profile: PersonalProfile) =>
    (segment: TimeDependentSegmentPayload, index: number): InsulinPumpSegmentDTO => {
      return {
        start_time: DateUtils.formatPumpMillis(segment.startTime),
        end_time:
          index + 1 >= profile.segments.length
            ? DateUtils.formatPumpMillis(24 * 60 * 60 * 1000 - 1)
            : DateUtils.formatPumpMillis(profile.segments[index + 1].startTime),
        basal_rate: segment.basalRate,
        insulin_to_carb_ratio: segment.carbRatio,
        sensitivity: segment.ISF,
        target_blood_glucose: segment.TargetBG,
      };
    };

  _getProfile = (profile: PersonalProfile, index: number): InsulinPumpProfileDTO => {
    const segments: InsulinPumpSegmentDTO[] = profile.segments.map(this._getSegment(profile));
    return {
      id: profile.profileId.toString(),
      current_profile: index === 0,
      name: profile.name,
      active_insulin_time: profile.insulinDuration,
      basal_insulin_per_day: getBasalInsulinPerDay(segments),
      segments,
    };
  };
  transform = (data: TandemData): Devices => {
    return {
      pumpDevice: {
        display_name: data.settings.controlIq
          ? TandemConstants.CLOSED_LOOP_DISPLAY_NAME
          : TandemConstants.OPEN_LOOP_DISPLAY_NAME,
        device_type_name: data.settings.controlIq
          ? TandemConstants.API_DEVICE_NAME_CLOSED
          : TandemConstants.API_DEVICE_NAME_OPEN,
        serial_number: data.versions.pumpSerial.toString(),
        last_upload: DateTime.now().toISO(),
        last_upload_info: {
          last_fetched_sequence: data.events.endSeq,
        } satisfies TandemLastUploadInfo,
        pump_data: {
          firmware_version: data.versions.firmwareVersion.toString(),
          profiles: data.settings.profiles.map(this._getProfile),
          closed_loop_support: data.settings.controlIq ? "available" : "unavailable",
        },
        closed_loop_support: data.settings.controlIq ? "available" : "unavailable",
      },
      cgmDevice: {
        display_name: TandemConstants.CGM_DISPLAY_NAME,
        device_type_name: TandemConstants.CGM_DEVICE_NAME,
        serial_number: data.versions.pumpSerial.toString(),
        last_upload: DateTime.now().toISO(),
        last_upload_info: {
          last_fetched_sequence: data.events.endSeq,
        },
      },
    };
  };
}
